import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { getFadeColor } from '@repo/toolkit/colors'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { DotIcon, Share2Icon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { TFunction } from 'i18next'
import { MouseEvent, useRef } from 'react'

import {
  diffuseHostFamilyAccommodations,
  diffuseOlInfos,
  diffuseTeams,
  diffuseTravelInformations
} from '../../../api/affected-staffs/diffusion-actions'
import { API } from '../../../api/apis.config'
import CountryFlag from '../../../components/elements/country-flag'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import InformationToolTip from '../../../components/elements/information-tooltip'
import UserNavBar from '../../../components/elements/nav-bar'
import { QualificationBadge } from '../../../components/elements/qualification-badge'
import { Paths } from '../../../lib/constants/path'
import useTranslate from '../../../lib/hooks/use-translate'
import { AffectedStaffStatusEnum } from '../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { DiffusionStatusEnum } from '../../../lib/types/enums/affected-staff/diffusion-status.enum'
import { staffPositionEnumMapping } from '../../../lib/types/enums/staff/staff-position.enum'
import {
  AffectedStaffMap,
  GroupedDiffusionResponse
} from '../../../lib/types/models/affectedStaff/affected-staff'
import { AffectedStaffFilters } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { showAxiosError, toastDiffusionResult } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'

type TableRow = AffectedStaffMap['FULL']

const defaultFilters: AffectedStaffFilters = {
  statuses: [AffectedStaffStatusEnum.AFFECTED]
}

const diffusionStatusConfig: Record<
  DiffusionStatusEnum,
  { bgColor: string; label: string; textColor: string }
> = {
  [DiffusionStatusEnum.NOT_APPLICABLE]: {
    bgColor: tailwindColors.grey.light.DEFAULT,
    textColor: tailwindColors.grey.DEFAULT,
    label: 'Non applicable'
  },
  [DiffusionStatusEnum.NOT_AVAILABLE]: {
    bgColor: tailwindColors.red.light.DEFAULT,
    textColor: tailwindColors.red.DEFAULT,
    label: 'Info non disponible ou diffusion impossible'
  },
  [DiffusionStatusEnum.AVAILABLE]: {
    bgColor: tailwindColors.orange.light.DEFAULT,
    textColor: tailwindColors.orange.DEFAULT,
    label: 'Info disponible, non diffusée'
  },
  [DiffusionStatusEnum.DIFFUSED]: {
    bgColor: tailwindColors.green.light.DEFAULT,
    textColor: tailwindColors.green.DEFAULT,
    label: 'Info diffusée'
  }
}

function DiffusionStatusCell({
  status,
  diffusedAt
}: {
  status?: DiffusionStatusEnum
  diffusedAt?: Date | string | null
}) {
  if (!status) return '-'
  const { bgColor, label, textColor } = diffusionStatusConfig[status]
  const isDiffused = status === DiffusionStatusEnum.DIFFUSED

  return (
    <InformationToolTip
      contentVariant="secondary"
      icon={
        <div className="flex items-center gap-1 flex-col">
          {isDiffused && diffusedAt ? (
            <Badge
              textColor={textColor}
              color={bgColor}
              text={DateFormatter.DDMMYYYY(diffusedAt)}
            />
          ) : (
            <Badge text={<DotIcon color={textColor} />} color={bgColor} className="h-8" />
          )}
        </div>
      }
    >
      <Typography
        variant="label-medium"
        color={tailwindColors.black.DEFAULT}
        className="text-pretty text-center w-40"
      >
        {isDiffused && !diffusedAt ? 'Info diffusée, date indisponible' : label}
      </Typography>
    </InformationToolTip>
  )
}

// Badge stops click propagation, so badges in a cell must open the job post sheet themselves
function column(
  t: TFunction<AvailableLanguages, undefined>,
  openJobPost: (row: TableRow, event: MouseEvent) => void
): Column<TableRow>[] {
  return [
    {
      header: 'Nom',
      accessorKey: 'staff.lastName',
      enableSorting: true,
      cell: ({ row }) => row.original.staff.lastName.toUpperCase()
    },
    {
      header: 'Prénom',
      accessorKey: 'staff.firstName',
      enableSorting: true,
      cell: ({ row }) => StringFormatter.capitalize(row.original.staff.firstName)
    },
    {
      header: 'Poste',
      accessorKey: 'staff.position',
      enableSorting: true,
      cell: ({ row }) => t(staffPositionEnumMapping[row.original.staff.position])
    },
    {
      header: 'Qualification',
      accessorKey: 'staffRequest.qualification',
      enableSorting: true,
      cell: ({ row }) => (
        <QualificationBadge
          qualification={row.original.staffRequest.qualification}
          onClick={(event) => openJobPost(row.original, event)}
        />
      )
    },
    {
      header: 'Pays',
      accessorKey: 'staffRequest.country.code',
      enableSorting: true,
      // added "XXX" since coutry code may be undefined and CountryFlag needs at least a value for the country code to display the fallback of no country
      cell: ({ row }) => <CountryFlag code={row.original.staffRequest.country?.code ?? 'XXX'} />
    },
    {
      header: 'Centres',
      accessorKey: 'staffRequest.centers',
      cell: ({ row }) => {
        const centers = row.original.staffRequest.centers.map((center) => center.name)
        const deletedCenterName = row.original.staffRequest.oldCenter
        const allCenters = deletedCenterName ? [...centers, deletedCenterName] : centers

        return (
          <div className="flex flex-col gap-1">
            {allCenters.map((center, index) => (
              <Typography key={index} variant="secondary-body">
                {center}
              </Typography>
            ))}
          </div>
        )
      }
    },
    {
      header: 'Date',
      accessorKey: 'staffRequest.startDate',
      enableSorting: true,
      cell: ({ row }) => {
        const startDate = row.original.staffRequest.startDate
        const endDate = row.original.staffRequest.endDate
        const periods = row.original.staffRequest.centers
          .map((center) => center.centerToJourneys.map((ctj) => ctj.journey.date.period))
          .flat(2)

        const period = periods.length > 0 ? periods[0] : undefined
        const color = period?.color ? getFadeColor(period?.color) : undefined
        const textColor = period?.color

        if (!startDate || !endDate) return '-'
        return (
          <Badge
            color={color}
            textColor={textColor}
            text={DateFormatter.getDateRangeDDMMYYYY(startDate, endDate)}
            onClick={(event) => openJobPost(row.original, event)}
          />
        )
      }
    },
    {
      accessorKey: 'staffRequest.formulas',
      header: 'Formule',
      cell: ({ row }) => {
        const formulas = row.original.staffRequest.formulas
        return (
          <div className="text-wrap w-36">
            {formulas.length > 0
              ? formulas.map((formula) => getTranslatedName(formula)).join(', ')
              : '-'}
          </div>
        )
      }
    },
    {
      accessorKey: 'trip',
      header: 'Voyage',
      enableSorting: true,
      cell: ({ row }) => (
        <Typography variant="secondary-body">
          {row.original.trip ? TripProperties.getTripLabel(row.original.trip) : '-'}
        </Typography>
      )
    },
    {
      accessorKey: 'travelInformationDiffused',
      header: 'Infos voyage',
      centerCell: true,
      cell: ({ row }) => (
        <DiffusionStatusCell
          status={row.original.diffusionStatuses?.travelInformation}
          diffusedAt={row.original.travelInformationDiffusedAt}
        />
      )
    },
    {
      accessorKey: 'olInfoDiffused',
      header: 'Infos OL',
      centerCell: true,
      cell: ({ row }) => (
        <DiffusionStatusCell
          status={row.original.diffusionStatuses?.olInfo}
          diffusedAt={row.original.olInfoDiffusedAt}
        />
      )
    },
    {
      accessorKey: 'hostFamilyAccommodationDiffused',
      header: 'Infos Hébergement',
      centerCell: true,
      cell: ({ row }) => (
        <DiffusionStatusCell
          status={row.original.diffusionStatuses?.hostFamilyAccommodation}
          diffusedAt={row.original.hostFamilyAccommodationDiffusedAt}
        />
      )
    },
    {
      accessorKey: 'teamDiffused',
      header: 'Infos Equipe',
      centerCell: true,
      cell: ({ row }) => (
        <DiffusionStatusCell
          status={row.original.diffusionStatuses?.team}
          diffusedAt={row.original.teamDiffusedAt}
        />
      )
    }
  ]
}

export function AffectedStaffDiffusionsListPage() {
  const { t } = useTranslate()
  const { navigateWithEvent } = useNavigator()
  const tableRef = useRef<DynamicTableRef<TableRow>>(null)

  const openJobPost = (row: TableRow, event?: MouseEvent) =>
    navigateWithEvent(Paths.AFFECTED_STAFFS_VIEW_PAGE.replace(':id', row.id.toString()), event)

  // the staffs that could not receive the information are named by the backend with the reason
  const showGroupedDiffusionResult = (result: GroupedDiffusionResponse) => {
    toastDiffusionResult(result)
    tableRef.current?.refetch()
  }

  const { mutateAsync: diffuseOlInfosMutation } = useMutation({
    mutationFn: diffuseOlInfos,
    onError: showAxiosError,
    onSuccess: showGroupedDiffusionResult
  })

  const { mutateAsync: diffuseTeamsMutation } = useMutation({
    mutationFn: diffuseTeams,
    onError: showAxiosError,
    onSuccess: showGroupedDiffusionResult
  })

  const { mutateAsync: diffuseHostFamilyAccommodationsMutation } = useMutation({
    mutationFn: diffuseHostFamilyAccommodations,
    onError: showAxiosError,
    onSuccess: showGroupedDiffusionResult
  })

  const { mutateAsync: diffuseTravelInformationsMutation } = useMutation({
    mutationFn: diffuseTravelInformations,
    onError: showAxiosError,
    onSuccess: showGroupedDiffusionResult
  })

  return (
    <div className="flex flex-col gap-4">
      <UserNavBar title={'Diffusions infos aux ME'} icon={Share2Icon} />

      <DynamicDataTable<TableRow, AffectedStaffFilters>
        ref={tableRef}
        selectable
        displayOrder
        title="Diffusions infos aux ME"
        columns={column(t, openJobPost)}
        allowSearchParam
        onRowClick={openJobPost}
        version="FULL"
        endpoint={API.affectedStaffs.getAll.url}
        filters={defaultFilters}
        permissions={{ delete: false }}
        actions={[
          {
            type: 'multiple',
            label: 'Diffuser le voyage',
            variant: 'ghost',
            fn: async (as) => {
              await diffuseTravelInformationsMutation(as.map((af) => af.id))
            }
          },
          {
            type: 'multiple',
            label: 'Diffuser OL Infos',
            variant: 'ghost',
            fn: async (as) => {
              await diffuseOlInfosMutation(as.map((af) => af.id))
            }
          },
          {
            type: 'multiple',
            label: 'Diffuser info hébergement',
            variant: 'ghost',
            fn: async (as) => {
              await diffuseHostFamilyAccommodationsMutation(as.map((af) => af.id))
            }
          },
          {
            type: 'multiple',
            label: "Diffuser l'équipe",
            variant: 'ghost',
            fn: async (as) => {
              await diffuseTeamsMutation(as.map((af) => af.id))
            }
          }
        ]}
      />
    </div>
  )
}
